// src/components/ReadhitComponent/MapChartComponent.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import _ from 'lodash';
import worldGeoJson  from '../../assets/Map/world_countries.json';
import { COUNTRY_CODES } from '../../assets/Map/COUNTRY_CODES';
import UserGuidMapChart from '../../utils/UserGuid/UserGuidMapChart.js';

const DATA_LIMIT = 150000;

const PIDENT_COLORS = [
  { min: 95,  color: '#00C853', label: '> 95%'   },
  { min: 90,  color: '#64DD17', label: '90–95%'  },
  { min: 85,  color: '#AEEA00', label: '85–90%'  },
  { min: 80,  color: '#FFD600', label: '80–85%'  },
  { min: 75,  color: '#FFAB00', label: '75–80%'  },
  { min: 0,   color: '#FF6D00', label: '< 75%'   },
];

function getPidentColor(pident) {
  for (const { min, color } of PIDENT_COLORS) if (pident > min) return color;
  return '#FF6D00';
}

function extractPrefix(sseqid) {
  const p = (sseqid || '').split('_');
  return p.length >= 2 ? `${p[0]}_${p[1]}` : sseqid;
}

function MapChartComponent({ data }) {
  const [guideOpen, setGuideOpen] = useState(false);
  const [originalData, setOriginalData]   = useState([]);
  const [filteredData, setFilteredData]   = useState([]);
  const [countryData,  setCountryData]    = useState({});
  const [loading,      setLoading]        = useState(true);
  const [error,        setError]          = useState(null);
  const [limitWarn,    setLimitWarn]      = useState(false);

  // Filters
  const [pidentRange,     setPidentRange]     = useState([0, 100]);
  const [lenRange,        setLenRange]        = useState([0, 1000]);
  const [virusTypes,      setVirusTypes]      = useState([]);
  const [selVirus,        setSelVirus]        = useState([]);
  const [seqTypes,        setSeqTypes]        = useState([]);
  const [selSeq,          setSelSeq]          = useState([]);

  // Tooltip
  const [tooltip, setTooltip] = useState(null);

  // ── Process raw data ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!data?.length) { setLoading(false); return; }
    setLoading(true); setError(null);

    try {
      let src = data;
      if (src.length > DATA_LIMIT) { setLimitWarn(true); src = src.slice(0, DATA_LIMIT); }
      else setLimitWarn(false);

      const processed = src.map(item => {
        const sseqid = item.sseqid || '';
        let countryCode = null;
        for (const pat of [/_([A-Z]{3})[\d\-_]/, /([A-Z]{3})[\d\-_]/]) {
          const m = sseqid.match(pat);
          if (m && COUNTRY_CODES[m[1]]) { countryCode = m[1]; break; }
        }
        if (!countryCode) {
          for (const code of ['BRA','YEM','UGA','PAK','SDN']) {
            if (sseqid.includes(code)) { countryCode = code; break; }
          }
        }
        const virusMatch = sseqid.match(/PV[0-9]/);
        const seqMatch   = sseqid.match(/^([^_]+)/);
        return {
          ...item,
          countryCode,
          virusType: virusMatch ? virusMatch[0] : null,
          seqType:   seqMatch   ? seqMatch[1]   : null,
          coordinates: countryCode ? COUNTRY_CODES[countryCode]?.coordinates : null,
        };
      });

      const vt = _.uniq(processed.map(d => d.virusType).filter(Boolean)).sort();
      const st = _.uniq(processed.map(d => d.seqType).filter(Boolean)).sort();
      setVirusTypes(vt); if (!selVirus.length) setSelVirus(vt);
      setSeqTypes(st);   if (!selSeq.length)   setSelSeq(st);
      setOriginalData(processed);
    } catch (e) {
      setError('Error processing data.');
    } finally {
      setLoading(false);
    }
  }, [data]);

  // ── Apply filters ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!originalData.length) return;
    const filtered = originalData.filter(d => {
      const pident = parseFloat(d.pident) || 0;
      const len    = Math.max(parseInt(d.qlen) || 0, parseInt(d.len) || 0);
      if (pident < pidentRange[0] || pident > pidentRange[1]) return false;
      if (len    < lenRange[0]    || len    > lenRange[1])    return false;
      if (d.virusType && !selVirus.includes(d.virusType))     return false;
      if (d.seqType   && !selSeq.includes(d.seqType))         return false;
      return true;
    }).filter(d => d.countryCode && d.coordinates);

    setFilteredData(filtered);

    const byCountry = _.groupBy(filtered, 'countryCode');
    const metrics   = {};
    Object.keys(byCountry).forEach(code => {
      const items = byCountry[code];
      metrics[code] = {
        code,
        name:    COUNTRY_CODES[code]?.name || code,
        pident:  _.meanBy(items, d => parseFloat(d.pident) || 0),
        count:   items.length,
        samples: items.slice(0, 5),
      };
    });
    setCountryData(metrics);
  }, [originalData, pidentRange, lenRange, selVirus, selSeq]);

  const reset = () => {
    setPidentRange([0, 100]); setLenRange([0, 1000]);
    setSelVirus(virusTypes); setSelSeq(seqTypes);
  };

  const avgPident = useMemo(() => {
    const vals = Object.values(countryData);
    return vals.length ? _.meanBy(vals, c => c.pident).toFixed(2) : 'N/A';
  }, [countryData]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="rh-chart-root" style={{ height: '100%' }}>

      {/* ── Filter panel ── */}
      <div className="rh-chart-panel" style={{ width: 220 }}>
        <div className="rh-chart-panel-title">
          Data Filters
          <button className="rh-btn-reset" onClick={reset}>↺ Reset</button>
          <button className="rh-btn-reset" onClick={() => setGuideOpen(true)}>? Guide</button>
        </div>

        {limitWarn && <div className="rh-alert rh-alert-warn">Limited to {DATA_LIMIT.toLocaleString()} rows</div>}
        {error     && <div className="rh-alert rh-alert-error">{error}</div>}

        <div className="rh-field">
          <label className="rh-label">Percent Identity ({pidentRange[0]}–{pidentRange[1]}%)</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <input type="range" className="rh-range" min={0} max={100} step={0.5}
              value={pidentRange[0]} onChange={e => setPidentRange([+e.target.value, pidentRange[1]])} />
            <input type="range" className="rh-range" min={0} max={100} step={0.5}
              value={pidentRange[1]} onChange={e => setPidentRange([pidentRange[0], +e.target.value])} />
          </div>
        </div>

        <div className="rh-field">
          <label className="rh-label">Seq Length ({lenRange[0]}–{lenRange[1]} bp)</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <input type="range" className="rh-range" min={0} max={1000} step={10}
              value={lenRange[0]} onChange={e => setLenRange([+e.target.value, lenRange[1]])} />
            <input type="range" className="rh-range" min={0} max={1000} step={10}
              value={lenRange[1]} onChange={e => setLenRange([lenRange[0], +e.target.value])} />
          </div>
        </div>

        {seqTypes.length > 0 && (
          <div className="rh-field">
            <label className="rh-label">Sequence Type</label>
            <div style={{ maxHeight: 100, overflowY: 'auto' }}>
              {seqTypes.map(t => (
                <label key={t} className="rh-checkbox-row">
                  <input type="checkbox" checked={selSeq.includes(t)}
                    onChange={() => setSelSeq(s => s.includes(t) ? s.filter(x => x !== t) : [...s, t])} />
                  {t}
                </label>
              ))}
            </div>
          </div>
        )}

        {virusTypes.length > 0 && (
          <div className="rh-field">
            <label className="rh-label">Virus Type</label>
            {virusTypes.map(t => (
              <label key={t} className="rh-checkbox-row">
                <input type="checkbox" checked={selVirus.includes(t)}
                  onChange={() => setSelVirus(s => s.includes(t) ? s.filter(x => x !== t) : [...s, t])} />
                {t}
              </label>
            ))}
          </div>
        )}

        <hr />

        <div className="rh-chart-panel-title" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
          Color Legend
          {PIDENT_COLORS.map(({ color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--rh-text)' }}>
              <span className="rh-swatch" style={{ background: color, width: 14, height: 14 }} />
              {label}
            </div>
          ))}
        </div>

        <hr />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            ['Sequences', filteredData.length],
            ['Countries', Object.keys(countryData).length],
            ['Avg Identity', `${avgPident}%`],
          ].map(([label, val]) => (
            <div key={label} className="rh-stat-box">
              <div className="rh-stat-box-label">{label}</div>
              <div className="rh-stat-box-value">{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Map area ── */}
      <div className="rh-chart-area" style={{ position: 'relative' }}>
        <div className="rh-chart-area-header">
          <div className="rh-chart-area-title">World Map Visualization</div>
          <div className="rh-chart-area-subtitle">
            {filteredData.length.toLocaleString()} sequences across {Object.keys(countryData).length} countries
          </div>
        </div>

        {loading ? (
          <div className="rh-empty"><div className="rh-spinner" /><span>Processing…</span></div>
        ) : (
          <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            <ComposableMap projectionConfig={{ scale: 147, center: [0, 0] }}
              style={{ width: '100%', height: '100%' }}>
              <Geographies geography={worldGeoJson}>
                {({ geographies }) => geographies.map(geo => {
                  const iso  = geo.properties.ISO_A3 || geo.properties.iso_a3;
                  let country = countryData[iso];
                  if (!country) {
                    const name = geo.properties.NAME || geo.properties.name;
                    const code = Object.keys(COUNTRY_CODES).find(c => COUNTRY_CODES[c]?.name === name);
                    if (code) country = countryData[code];
                  }
                  return (
                    <Geography key={geo.rsmKey} geography={geo}
                      onMouseEnter={() => country && setTooltip(country)}
                      onMouseLeave={() => setTooltip(null)}
                      style={{
                        default: { fill: country ? getPidentColor(country.pident) : '#D6D6DA', stroke: '#fff', strokeWidth: 0.5, outline: 'none', cursor: country ? 'pointer' : 'default' },
                        hover:   { fill: '#1d4ed8', stroke: '#fff', strokeWidth: 0.7, outline: 'none' },
                        pressed: { fill: '#1e40af', outline: 'none' },
                      }} />
                  );
                })}
              </Geographies>
            </ComposableMap>

            {/* Tooltip */}
            {tooltip && (
              <div className="rh-map-tooltip" style={{ top: 20, left: 20 }}>
                <div className="rh-map-tooltip-title">{tooltip.name} ({tooltip.code})</div>
                <div className="rh-map-tooltip-row">
                  <span>Sequences</span>
                  <span className="rh-map-tooltip-val">{tooltip.count}</span>
                </div>
                <div className="rh-map-tooltip-row">
                  <span>Avg Identity</span>
                  <span className="rh-map-tooltip-val">{tooltip.pident.toFixed(2)}%</span>
                </div>
                {tooltip.samples?.length > 0 && (
                  <>
                    <div style={{ marginTop: 6, marginBottom: 2, fontSize: 10, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase' }}>Samples</div>
                    {tooltip.samples.map((s, i) => (
                      <div key={i} style={{ fontSize: 10, color: 'var(--rh-text-muted)', fontFamily: 'var(--rh-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.sseqid}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <UserGuidMapChart open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}
export default React.memo(MapChartComponent);
